<template>
    <Dialog :open="open" :title="$t('alerts.scheduleDetails')" max-width="900" @close="$emit('close')">
        <v-container class="pa-0 pb-2">
            <v-form ref="form">
                <v-row>
                    <v-col class="pb-0 mb-3" cols="6">
                        <v-text-field :label="$t('date')" :value="formatDateTime(alert.CreatedAt)" disabled readonly/>
                        <v-text-field :label="$t('notifications.severity')" :value="alert.Severity" disabled readonly/>
                        <v-text-field :label="$t('notifications.title')" :value="alert.Title" disabled readonly/>
                        <v-textarea :label="$t('authentication.address')" :value="alert.Address" disabled hide-details
                                    readonly
                                    rows="3" class="mb-6"/>
                        <v-text-field :label="$t('settings.timezone')" :value="alert.Timezone" disabled readonly/>
                        <v-text-field :label="$t('alerts.gracePeriod')" :value="alert.GracePeriod" disabled readonly/>
                        <v-text-field label="Number of occurrences / current number of occurrences"
                                      :value="alert.NoOfOccurrences + '/' + alert.CurrentNoOfOccurrences " disabled
                                      readonly/>
                        <v-textarea :label="$t('alerts.details')" :value="alert.Text" class="mb-8" disabled hide-details
                                    readonly
                                    rows="3"/>
                    </v-col>
                    <v-col class="pb-0" cols="6">
                        <v-text-field :label="$t('startDate')" :value="formatDateTime(alert.StartDate)" disabled
                                      readonly/>
                        <div v-if="alert.Recurring">
                            <v-text-field :label="$t('endDate')" :value="formatDateTime(alert.StartDate)" disabled
                                          readonly/>
                            <v-text-field :label="$t('alerts.day')" :value="alert.Day" disabled readonly/>
                            <v-text-field :label="$t('alerts.dayOfWeek')" :value="alert.DayOfWeek" disabled readonly/>
                            <v-text-field :label="$t('alerts.times')" :value="alert.Times.join(', ')" disabled
                                          readonly/>
                        </div>
                        <v-text-field v-if="location" :label="$t('alerts.alertCreationLocation')" :value="location" disabled readonly/>
                        <div v-else class="primary--text text-center mt-2">{{ $t('alerts.noLocationData') }}</div>
                        <div class="map-block">
                            <MapBasic v-if="location " :markers="markers" before-leave click-full-screen/>
                        </div>
                    </v-col>
                </v-row>
            </v-form>
        </v-container>
    </Dialog>
</template>

<script>
import Dialog from "@/components/reusable/Dialog/Dialog";
import {formatDateTime} from "@/helper/helper";
import {
  CHANNEL_EMAIL,
  CHANNEL_REALTIME,
  CHANNEL_SMS,
  NOTIFICATION_COMPLETED,
  NOTIFICATION_FAILED,
  NOTIFICATION_PENDING
} from "@/helper/constants";
import MapBasic from "@/components/MapBasic/MapBasic";

export default {
  name: "AlertScheduledDetails",
  components: {
    MapBasic,
    Dialog,
  },
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    alertID: {
      type: Number,
      required: false,
    },
    alert: {
      type: Object,
      required: true,
    }
  },
  computed: {
    isVideoPayload() {
      if (!this.alert.PayloadUrl) {
        return false;
      }
      return ['.mp4', '.avi', '.mov', '.wmv', '.vwm'].includes(this.alert.PayloadUrl.substr(this.alert.PayloadUrl.lastIndexOf('.')));
    },
    isAudioPayload() {
      if (!this.alert.PayloadUrl) {
        return false;
      }
      return ['.mp3', '.wav'].includes(this.alert.PayloadUrl.substr(this.alert.PayloadUrl.lastIndexOf('.')));
    },
    isImagePayload() {
      if (!this.alert.PayloadUrl) {
        return false;
      }
      return ['.jpg', '.jpeg', '.bmp', '.png'].includes(this.alert.PayloadUrl.substr(this.alert.PayloadUrl.lastIndexOf('.')));
    },
    markers() {
      return this.alert.Location ? [{lat: this.alert.Location.Latitude, lng: this.alert.Location.Longitude}] : [];
    },
    location() {
      return this.alert.Location && (this.alert.Location.Latitude || this.alert.Location.Latitude) ? this.alert.Location.Latitude + ', ' + this.alert.Location.Longitude : '';
    },
    filteredRecipients() {
      return this.recipients.filter((recipient) => this.filters.channels.includes(recipient.Channel) && this.filters.status.includes(recipient.Status) && this.filters.circles.includes(recipient.CircleName));
    },
    header() {
      return [
        {text: this.$t('circles.avatar'), value: 'AvatarUrl'},
        {text: this.$t('authentication.firstName'), value: 'FirstName'},
        {text: this.$t('authentication.lastName'), value: 'LastName'},
        {text: this.$t('authentication.email'), value: 'Email'},
        {text: this.$t('menu.circles'), value: 'CircleName'},
        {text: this.$t('notifications.channel'), value: 'Channel'},
        {text: this.$t('status'), value: 'Status'},
      ];
    },
    channels() {
      return [
        {
          value: CHANNEL_SMS,
          text: this.$t('settings.SMS')
        },
        {
          value: CHANNEL_REALTIME,
          text: this.$t('settings.Realtime')
        },
        {
          value: CHANNEL_EMAIL,
          text: this.$t('settings.Email')
        },
      ];
    },
    statuses() {
      return [
        {
          value: NOTIFICATION_PENDING,
          text: this.$t('notifications.pending')
        },
        {
          value: NOTIFICATION_COMPLETED,
          text: this.$t('notifications.completed')
        },
        {
          value: NOTIFICATION_FAILED,
          text: this.$t('notifications.failed')
        },
      ];
    },
  },
  watch: {
    alertID(id) {
      if (id) {
        this.getAlertDetails(id);
      }
    },
  },
  mounted() {
    if (this.alertID) {
      this.getAlertDetails(this.alertID);
    }
  },
  data() {
    return {
      recipients: [],
      loading: false,
      filters: {
        channels: [],
        status: [],
        circles: [],
      },
      availableValues: {
        channels: new Set(),
        status: new Set(),
        circles: new Set(),
      },
      circles: [],
    };
  },
  methods: {
    formatDateTime,
    getAlertDetails(id) {
      const url = this.$store.getters.alertRecipients(id);
      this.loading = true;
      this.recipients = [];
      this.axios.get(url).then(({data}) => {
        this.recipients = data.Result;
        this.availableValues.channels.clear();
        this.availableValues.status.clear();
        this.recipients.forEach(recipient => {
          this.availableValues.channels.add(recipient.Channel);
          this.availableValues.status.add(recipient.Status);
          if (!this.availableValues.circles.has(recipient.CircleName)) {
            this.circles.push({value: recipient.CircleName, text: recipient.CircleName});
          }
          this.availableValues.circles.add(recipient.CircleName);

        });
      }).finally(() => {
        this.loading = false;
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import "alert-details";
</style>